import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Shield, Building2, Eye, EyeOff, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Login = () => {
  const [mode, setMode] = useState('tenant'); // 'tenant' | 'superadmin'
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', tenantSlug: '' });
  const { loginSuperAdmin, loginTenant } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'superadmin') {
        await loginSuperAdmin(form.email, form.password);
        toast.success('Welcome, Super Admin!');
        navigate('/super-admin');
      } else {
        if (!form.tenantSlug) { toast.error('Organization ID is required'); setLoading(false); return; }
        await loginTenant(form.email, form.password, form.tenantSlug);
        toast.success('Welcome back!');
        navigate('/app');
      }
    } catch (err) {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur rounded-2xl mb-4 shadow-xl">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">DocVault DMS</h1>
          <p className="text-primary-200 mt-1">Document Management System</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Tabs */}
          <div className="flex">
            <button
              onClick={() => setMode('tenant')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors ${mode === 'tenant' ? 'bg-primary-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
            >
              <Building2 className="w-4 h-4" /> Organization Login
            </button>
            <button
              onClick={() => setMode('superadmin')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors ${mode === 'superadmin' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
            >
              <Shield className="w-4 h-4" /> Super Admin
            </button>
          </div>

          <div className="p-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              {mode === 'superadmin' ? 'System Administrator Access' : 'Sign in to your organization'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'tenant' && (
                <div>
                  <label className="label">Organization ID (Slug)</label>
                  <input
                    className="input"
                    placeholder="e.g. acme-corp"
                    value={form.tenantSlug}
                    onChange={e => setForm({ ...form, tenantSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    required
                  />
                </div>
              )}
              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  className="input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    className="input pr-10"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-2.5 rounded-lg font-medium text-white transition-colors flex items-center justify-center gap-2 ${
                  mode === 'superadmin' ? 'bg-gray-900 hover:bg-gray-800' : 'bg-primary-600 hover:bg-primary-700'
                } disabled:opacity-60`}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            {mode === 'tenant' && (
              <p className="mt-4 text-center text-sm text-gray-500">
                Don't have an organization?{' '}
                <Link to="/register" className="font-medium text-primary-600 hover:underline">Register</Link>
              </p>
            )}
          </div>
        </div>
        <p className="text-center text-primary-200 text-xs mt-6">© 2025 DocVault DMS. All rights reserved.</p>
      </div>
    </div>
  );
};

export default Login;
